import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { API } from "@/utils/constants";

export const VehicleFormModal = ({ open, onClose, vehicle, onSave }) => {
  const [formData, setFormData] = useState({
    unit_number: "",
    vin: "",
    vehicle_type: "tractor",
    year: new Date().getFullYear(),
    make: "",
    model: "",
    engine_year: null,
    engine_make: "",
    engine_model: "",
    engine_serial: "",
    transmission_make: "",
    transmission_model: "",
    transmission_type: "",
    fuel_type: "diesel",
    def_equipped: true,
    mileage: "",
    engine_hours: "",
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (vehicle) {
      setFormData({
        unit_number: vehicle.unit_number || "",
        vin: vehicle.vin || "",
        vehicle_type: vehicle.vehicle_type || "tractor",
        year: vehicle.year || new Date().getFullYear(),
        make: vehicle.make || "",
        model: vehicle.model || "",
        engine_year: vehicle.engine_year || null,
        engine_make: vehicle.engine_make || "",
        engine_model: vehicle.engine_model || "",
        engine_serial: vehicle.engine_serial || "",
        transmission_make: vehicle.transmission_make || "",
        transmission_model: vehicle.transmission_model || "",
        transmission_type: vehicle.transmission_type || "",
        fuel_type: vehicle.fuel_type || "diesel",
        def_equipped: vehicle.def_equipped !== undefined ? vehicle.def_equipped : true,
        mileage: vehicle.mileage > 0 ? vehicle.mileage.toLocaleString() : "",
        engine_hours: vehicle.engine_hours > 0 ? vehicle.engine_hours.toLocaleString() : "",
      });
    } else {
      setFormData({
        unit_number: "",
        vin: "",
        vehicle_type: "tractor",
        year: new Date().getFullYear(),
        make: "",
        model: "",
        engine_year: null,
        engine_make: "",
        engine_model: "",
        engine_serial: "",
        transmission_make: "",
        transmission_model: "",
        transmission_type: "",
        fuel_type: "diesel",
        def_equipped: true,
        mileage: "",
        engine_hours: "",
      });
    }
  }, [vehicle, open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    // Convert formatted strings to numbers for API
    const submitData = {
      ...formData,
      mileage: parseInt(formData.mileage.toString().replace(/,/g, '')) || 0,
      engine_hours: parseInt(formData.engine_hours.toString().replace(/,/g, '')) || 0,
    };
    
    try {
      if (vehicle) {
        await axios.put(`${API}/vehicles/${vehicle.id}`, submitData);
        toast.success("Vehicle updated successfully");
      } else {
        await axios.post(`${API}/vehicles`, submitData);
        toast.success("Vehicle added successfully");
      }
      onSave();
      onClose();
    } catch (error) {
      toast.error("Failed to save vehicle");
      console.error(error);
    }
    setLoading(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="w-screen h-[100dvh] max-w-none max-h-[100dvh] m-0 rounded-none flex flex-col fixed inset-0 p-0 overflow-hidden">
        <DialogHeader className="flex-shrink-0 p-4 border-b bg-white">
          <DialogTitle className="text-lg">{vehicle ? "Edit Vehicle" : "Add Vehicle"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
          <div className="flex-1 overflow-y-auto p-4" style={{ WebkitOverflowScrolling: 'touch' }}>
            <div className="max-w-4xl mx-auto space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="unit_number">Unit Number *</Label>
                <Input
                  id="unit_number"
                  data-testid="input-unit-number"
                  value={formData.unit_number}
                  onChange={(e) => setFormData({ ...formData, unit_number: e.target.value })}
                  required
                  className="mt-1 h-11"
                />
              </div>
              <div>
                <Label htmlFor="vin">VIN *</Label>
                <Input
                  id="vin"
                  data-testid="input-vin"
                  value={formData.vin}
                  onChange={(e) => setFormData({ ...formData, vin: e.target.value })}
                  required
                  className="mt-1 h-11"
                />
              </div>
            </div>
          
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="vehicle_type">Vehicle Type *</Label>
                <Select 
                  value={formData.vehicle_type} 
                  onValueChange={(v) => setFormData({ ...formData, vehicle_type: v })}
                >
                  <SelectTrigger data-testid="select-vehicle-type" className="mt-1 h-11">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="tractor">Tractor</SelectItem>
                    <SelectItem value="chassis">Chassis</SelectItem>
                    <SelectItem value="yard_equipment">Yard Equipment</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="year">Year *</Label>
                <Input
                  id="year"
                  type="number"
                  data-testid="input-year"
                  value={formData.year}
                  onChange={(e) => setFormData({ ...formData, year: parseInt(e.target.value) })}
                  required
                  className="mt-1 h-11"
                />
              </div>
            </div>
          
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="make">Make *</Label>
                <Input
                  id="make"
                  data-testid="input-make"
                  value={formData.make}
                  onChange={(e) => setFormData({ ...formData, make: e.target.value })}
                  required
                  className="mt-1 h-11"
                />
              </div>
              <div>
                <Label htmlFor="model">Model *</Label>
                <Input
                  id="model"
                  data-testid="input-model"
                  value={formData.model}
                  onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                  required
                  className="mt-1 h-11"
                />
              </div>
            </div>

            <div className="border-t pt-4">
              <h4 className="font-medium text-slate-700 mb-3 text-sm">Engine Info</h4>
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div>
                  <Label htmlFor="engine_year">Engine Year</Label>
                  <Input
                    id="engine_year"
                    type="number"
                    data-testid="input-engine-year"
                    value={formData.engine_year || ""}
                    onChange={(e) => setFormData({ ...formData, engine_year: e.target.value ? parseInt(e.target.value) : null })}
                    className="mt-1 h-11"
                  />
                </div>
                <div>
                  <Label htmlFor="engine_make">Engine Make</Label>
                  <Input
                    id="engine_make"
                    data-testid="input-engine-make"
                    value={formData.engine_make}
                    onChange={(e) => setFormData({ ...formData, engine_make: e.target.value })}
                    className="mt-1 h-11"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="engine_model">Engine Model</Label>
                  <Input
                    id="engine_model"
                    data-testid="input-engine-model"
                    value={formData.engine_model}
                    onChange={(e) => setFormData({ ...formData, engine_model: e.target.value })}
                    className="mt-1 h-11"
                  />
                </div>
                <div>
                  <Label htmlFor="engine_serial">Engine Serial</Label>
                  <Input
                    id="engine_serial"
                    data-testid="input-engine-serial"
                    value={formData.engine_serial}
                    onChange={(e) => setFormData({ ...formData, engine_serial: e.target.value })}
                    className="mt-1 h-11"
                  />
                </div>
              </div>
            </div>

            <div className="border-t pt-4">
              <h4 className="font-medium text-slate-700 mb-3 text-sm">Transmission Info</h4>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label htmlFor="transmission_make">Make</Label>
                  <Input
                    id="transmission_make"
                    data-testid="input-transmission-make"
                    value={formData.transmission_make}
                    onChange={(e) => setFormData({ ...formData, transmission_make: e.target.value })}
                    className="mt-1 h-11"
                  />
                </div>
                <div>
                  <Label htmlFor="transmission_model">Model</Label>
                  <Input
                    id="transmission_model"
                    data-testid="input-transmission-model"
                    value={formData.transmission_model}
                    onChange={(e) => setFormData({ ...formData, transmission_model: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="transmission_type">Type</Label>
                  <Select 
                    value={formData.transmission_type} 
                    onValueChange={(v) => setFormData({ ...formData, transmission_type: v })}
                  >
                    <SelectTrigger data-testid="select-transmission-type">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="automatic">Automatic</SelectItem>
                      <SelectItem value="standard">Standard</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <div className="border-t pt-6">
              <h4 className="font-medium text-slate-700 mb-4">Fuel Configuration</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="fuel_type">Fuel Type</Label>
                  <Select 
                    value={formData.fuel_type} 
                    onValueChange={(v) => setFormData({ ...formData, fuel_type: v })}
                  >
                    <SelectTrigger data-testid="select-fuel-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="diesel">Diesel</SelectItem>
                      <SelectItem value="cng">CNG</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      data-testid="checkbox-def"
                      checked={formData.def_equipped}
                      onChange={(e) => setFormData({ ...formData, def_equipped: e.target.checked })}
                      className="w-4 h-4 rounded border-slate-300"
                    />
                    <span className="text-sm">DEF System Equipped</span>
                  </label>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="mileage">Current Mileage</Label>
                <Input
                  id="mileage"
                  type="text"
                  inputMode="numeric"
                  data-testid="input-mileage"
                  value={formData.mileage}
                  onChange={(e) => {
                    const rawValue = e.target.value.replace(/[^0-9]/g, '');
                    if (rawValue === '') {
                      setFormData({ ...formData, mileage: '' });
                    } else {
                      const formatted = parseInt(rawValue).toLocaleString();
                      setFormData({ ...formData, mileage: formatted });
                    }
                  }}
                  placeholder="Enter mileage"
                />
              </div>
              {formData.fuel_type === "cng" && (
                <div>
                  <Label htmlFor="engine_hours">Engine Hours</Label>
                  <Input
                    id="engine_hours"
                    type="text"
                    inputMode="numeric"
                    data-testid="input-engine-hours"
                    value={formData.engine_hours}
                    onChange={(e) => {
                      const rawValue = e.target.value.replace(/[^0-9]/g, '');
                      if (rawValue === '') {
                        setFormData({ ...formData, engine_hours: '' });
                      } else {
                        const formatted = parseInt(rawValue).toLocaleString();
                        setFormData({ ...formData, engine_hours: formatted });
                      }
                    }}
                    placeholder="Enter engine hours"
                  />
                  <p className="text-xs text-slate-500 mt-1">Oil change: every 1,000 hours for CNG</p>
                </div>
              )}
            </div>

          </div>
          </div>
          <div className="flex-shrink-0 p-4 border-t bg-white flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading} data-testid="btn-save-vehicle">
              {loading ? "Saving..." : vehicle ? "Update Vehicle" : "Add Vehicle"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
